import { API_PREFIX } from '@hit-game/shared/constants';

import { siteConfig } from './config';

/**
 * Một kết nối SSE `GET /api/v1/events` DÙNG CHUNG cho cả app.
 *
 * Trước đây mỗi hook (`useSyncEvents`, `useJackpotEvents`) tự mở một `EventSource`
 * riêng → trên cùng một trang, mỗi user giữ nhiều kết nối, và server duplicate
 * một Redis subscriber cho mỗi kết nối. Ở đây gom về MỘT connection ref-counted:
 * mở khi có subscriber đầu tiên, đóng khi subscriber cuối rời đi, tự reconnect có
 * backoff. Dispatch theo TÊN event (`sync-completed`, `jackpot`, …).
 */

type RawHandler = (data: string) => void;

/** Backoff reconnect: 1s → 2s → 4s … tối đa 30s. */
const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

const handlers = new Map<string, Set<RawHandler>>();
const esListeners = new Map<string, (event: MessageEvent<string>) => void>();
let es: EventSource | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = INITIAL_RETRY_MS;

function attachEventListener(name: string): void {
  if (!es || esListeners.has(name)) return;
  const listener = (event: MessageEvent<string>): void => {
    const set = handlers.get(name);
    if (!set) return;
    for (const handler of set) handler(event.data);
  };
  esListeners.set(name, listener);
  es.addEventListener(name, listener);
}

function connect(): void {
  // Hủy backoff đang chờ: nếu không, một subscriber mới gọi connect() trong lúc
  // đang backoff sẽ mở EventSource ở đây, rồi retryTimer cũ vẫn fire mở thêm cái
  // nữa (leak + trùng event).
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (es) return; // đã có kết nối — không mở trùng

  es = new EventSource(`${siteConfig.apiUrl}${API_PREFIX}/events`, { withCredentials: true });
  for (const name of handlers.keys()) attachEventListener(name);

  es.onopen = () => {
    retryDelay = INITIAL_RETRY_MS; // Reset backoff khi kết nối lại được.
  };
  es.onerror = () => {
    es?.close();
    es = null;
    esListeners.clear();
    if (handlers.size === 0) return; // không còn ai nghe → thôi reconnect
    retryTimer = setTimeout(connect, retryDelay);
    retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
  };
}

function teardownIfIdle(): void {
  if (handlers.size > 0) return;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  es?.close();
  es = null;
  esListeners.clear();
  retryDelay = INITIAL_RETRY_MS;
}

/**
 * Đăng ký nghe một tên event trên connection dùng chung. Trả hàm huỷ đăng ký;
 * huỷ subscriber cuối cùng thì tự đóng connection.
 */
export function subscribeServerEvent(name: string, handler: RawHandler): () => void {
  let set = handlers.get(name);
  if (!set) {
    set = new Set();
    handlers.set(name, set);
  }
  set.add(handler);

  if (!es) connect();
  else attachEventListener(name);

  return () => {
    const current = handlers.get(name);
    current?.delete(handler);
    if (current && current.size === 0) {
      handlers.delete(name);
      const listener = esListeners.get(name);
      if (listener && es) es.removeEventListener(name, listener);
      esListeners.delete(name);
    }
    teardownIfIdle();
  };
}
